import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import TENANTS from '~/consts/tenant';

import { useAllocateHintProperties } from '~/hooks/apis/connection-google/useAllocateHintProperties';
import { hintCoverageGridQueryKeys } from '~/hooks/apis/connection-google/useHintCoverageGrid';
import { hintCoverageQueryKeys } from '~/hooks/apis/connection-google/useHintCoverageMatrix';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { AllocatedProperty, GHAPropertyBrand, HintCoverageAccountType } from '~/services/ConnectionGoogleService';

import { getAccountTypesForBrand } from '../GHAPropertyManagement/utils';

import AllocatedPropertiesSearch from './AllocatedPropertiesSearch';
import HintCoverageDetailedMatrix from './HintCoverageDetailedMatrix';
import HintCoverageGridSummary from './HintCoverageGridSummary';
import PropertyHintCoverageView from './PropertyHintCoverageView';

// LPP first: the first brand-allowed option doubles as the default when the URL has no
// accountType, so this order (mirroring GHAPropertyManagement's ACCOUNT_TYPE_ORDER) matters.
const ACCOUNT_TYPE_OPTIONS: Array<{ value: HintCoverageAccountType; label: string }> = [
  { value: 'lpp', label: 'LPP' },
  { value: 'alt', label: 'ALT' },
  { value: 'lpc', label: 'LPC' },
  { value: 'le_vacation_rental', label: 'LE Vacation Rental' },
  { value: 'scoopon', label: 'Scoopon' },
];

// Each GHA account belongs to exactly one portal tenant, so a URL's accountType can drive the
// brand switcher. Keyed by the full union so adding an account type forces a mapping here.
const TENANT_FOR_ACCOUNT: Record<HintCoverageAccountType, keyof typeof TENANTS> = {
  lpp: 'lux',
  alt: 'lux',
  lpc: 'lux',
  le_vacation_rental: 'lux',
  scoopon: 'scoopon',
};

const SUMMARY_TAB = 0;
const DETAILED_TAB = 1;

export default function HintCoverageMatrixContainer() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // The top-left brand switcher scopes the GHA accounts, same as GHA Property Management:
  // Scoopon brand → the scoopon account only; Luxury Escapes → lpp/alt/lpc/le_vacation_rental.
  // Brands with no GHA mapping fall back to every account so the page stays usable.
  const { tenant, onTenantSelect } = useCurrentTenant();
  const currentBrand = tenant?.brand;
  const accountTypeOptions = useMemo(() => {
    const brandAccountTypes = getAccountTypesForBrand(currentBrand as GHAPropertyBrand);
    const brandOptions = ACCOUNT_TYPE_OPTIONS.filter((opt) => brandAccountTypes.includes(opt.value));
    return brandOptions.length > 0 ? brandOptions : ACCOUNT_TYPE_OPTIONS;
  }, [currentBrand]);

  // The account type lives in the URL (single source of truth) so copied links, refreshes and
  // back/forward all restore the account the user was actually looking at — a shared Scoopon
  // link must not silently open as LPP. The URL value is honoured only when the current brand
  // allows it; otherwise the brand's first account applies (lpp for LE, scoopon for Scoopon),
  // which is what makes the brand switcher flip the whole page in one click.
  const accountTypeParam = searchParams.get('accountType')?.toLowerCase() ?? null;
  const paramOption = accountTypeOptions.find((opt) => opt.value === accountTypeParam);
  const accountType: HintCoverageAccountType = paramOption?.value ?? accountTypeOptions[0].value;

  const handleAccountTypeChange = (value: HintCoverageAccountType) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('accountType', value);
      return next;
    });
  };

  // Two-way sync between the URL param and the portal brand — whichever changed most recently
  // wins, so the two can never fight:
  //
  // URL → brand: a deep link, hand-edited param, or back/forward that names the OTHER brand's
  // account flips the portal brand via the same dispatch as the top-left switcher
  // (?accountType=lpp on the Scoopon brand switches the portal to Luxury Escapes). Skipped when
  // the brand itself just changed (the param is then the stale side), and skipped for tenants
  // with no GHA mapping (e.g. Cudo), which keep their existing view-any-account fallback.
  //
  // Brand → URL: otherwise the resolved account is written back whenever the param is missing
  // (first load), stale after a brand switch, or non-canonical (?accountType=LPP → lpp).
  // `replace` keeps these rewrites out of the history stack so Back never steps through them.
  const prevBrandRef = useRef(currentBrand);
  useEffect(() => {
    const brandChanged = prevBrandRef.current !== currentBrand;
    prevBrandRef.current = currentBrand;

    const paramAccount = ACCOUNT_TYPE_OPTIONS.find((opt) => opt.value === accountTypeParam)?.value;
    const paramTenant = paramAccount ? TENANT_FOR_ACCOUNT[paramAccount] : null;
    const brandHasGhaAccounts = getAccountTypesForBrand(currentBrand as GHAPropertyBrand).length > 0;
    if (!brandChanged && brandHasGhaAccounts && paramTenant && TENANTS[paramTenant].brand !== currentBrand) {
      onTenantSelect(paramTenant);
      return;
    }

    if (searchParams.get('accountType') !== accountType) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('accountType', accountType);
          return next;
        },
        { replace: true },
      );
    }
  }, [currentBrand, accountTypeParam, accountType, searchParams, setSearchParams, onTenantSelect]);
  const [windowHours, setWindowHours] = useState(5);
  const [activeTab, setActiveTab] = useState(SUMMARY_TAB);
  const { mutate: allocateHintProperties, isPending: isAllocating } = useAllocateHintProperties();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Property drill-down is driven by the propertyId query param so views are
  // deep-linkable — e.g. pasting a Hotel ID straight from a GHA price accuracy report.
  const propertyId = searchParams.get('propertyId');
  // The clicked row is remembered together with the account type it was listed under: allocation
  // metadata (tier, country, forced, opportunities) is account-specific, so the row must not be
  // reused when the drill-down's account type changes — the view falls back to its own lookup.
  const [selectedProperty, setSelectedProperty] = useState<{
    accountType: HintCoverageAccountType;
    property: AllocatedProperty;
  } | null>(null);

  const handlePropertySelect = (property: AllocatedProperty) => {
    setSelectedProperty({ accountType, property });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('propertyId', property.id);
      return next;
    });
  };

  const handlePropertyIdSubmit = (nextPropertyId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('propertyId', nextPropertyId);
      return next;
    });
  };

  const handleBackFromProperty = () => {
    setSelectedProperty(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('propertyId');
      return next;
    });
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: hintCoverageGridQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: hintCoverageQueryKeys.all });
  };

  return (
    <Container maxWidth="xl">
      {propertyId && (
        <>
          <Stack direction="row" justifyContent="flex-end" mt={1} mb={1}>
            <FormControl size="small" sx={{ minWidth: theme.spacing(22.5) }}>
              <InputLabel>Account Type</InputLabel>
              <Select<HintCoverageAccountType>
                value={accountType}
                label="Account Type"
                onChange={(e) => handleAccountTypeChange(e.target.value)}
              >
                {accountTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <PropertyHintCoverageView
            propertyId={propertyId}
            accountType={accountType}
            initialProperty={
              selectedProperty?.property.id === propertyId && selectedProperty.accountType === accountType
                ? selectedProperty.property
                : null
            }
            onBack={handleBackFromProperty}
            onPropertyIdSubmit={handlePropertyIdSubmit}
          />
        </>
      )}

      {/* Hidden rather than unmounted while the property view is open, so the account-level
          state (allocated-properties search inputs and results, active tab) survives Back. */}
      <Box sx={{ display: propertyId ? 'none' : undefined }}>
        <PageSubheader title="Pull With Hints Coverage">
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: theme.spacing(22.5) }}>
              <InputLabel>Account Type</InputLabel>
              <Select<HintCoverageAccountType>
                value={accountType}
                label="Account Type"
                onChange={(e) => handleAccountTypeChange(e.target.value)}
              >
                {accountTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Window (hrs)"
              type="number"
              value={windowHours}
              onChange={(e) => setWindowHours(Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
              slotProps={{ htmlInput: { min: 1, max: 24 } }}
              sx={{ width: theme.spacing(15) }}
            />

            <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={handleRefresh}>
              Refresh
            </Button>
          </Stack>
        </PageSubheader>

        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mt: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Summary" />
            <Tab label="Detailed Coverage" />
          </Tabs>
          <Button variant="contained" color="primary" onClick={() => setConfirmDialogOpen(true)}>
            Re-classify Properties
          </Button>
        </Box>

        {activeTab === SUMMARY_TAB && <HintCoverageGridSummary accountType={accountType} windowHours={windowHours} />}

        {activeTab === DETAILED_TAB && (
          <HintCoverageDetailedMatrix accountType={accountType} windowHours={windowHours} />
        )}

        <AllocatedPropertiesSearch accountType={accountType} onPropertySelect={handlePropertySelect} />

        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
          <DialogTitle>Confirm Re-classify Properties</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to re-classify properties for account type{' '}
              <strong>{accountType.toUpperCase()}</strong>? This will queue a background reclassification job.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              loading={isAllocating}
              onClick={() => {
                allocateHintProperties(accountType, {
                  onSuccess: () => setConfirmDialogOpen(false),
                  onError: () => setConfirmDialogOpen(false),
                });
              }}
            >
              {isAllocating ? 'Submitting…' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
